import {
  CustomToolMockResponseType,
  toolMockResponseMaxLength,
} from "@repo/app-types/entities/tools/custom";
import { FieldError } from "@repo/ui/field/fieldError";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { useId, type FC } from "react";
import type { ToolMockFormSettings } from "../formConfigs/toolMockResponse.js";
import { LazyCodeEditor } from "./lazyCodeEditor.js";

const mockResponseTypeOptions: SimpleSelectOption<CustomToolMockResponseType>[] =
  [
    { value: CustomToolMockResponseType.STRING, label: "String" },
    { value: CustomToolMockResponseType.JSON, label: "JSON" },
  ];

interface ToolMockResponseFieldProps {
  settings: ToolMockFormSettings;
  onSettingsChange: (settings: Partial<ToolMockFormSettings>) => void;
  /** Set while a save is in flight: the JSON editor takes no edits. */
  isReadOnly: boolean;
}

/** The response a tool gives in a simulation instead of running. */
export const ToolMockResponseField: FC<ToolMockResponseFieldProps> = (
  props,
) => {
  const { settings, onSettingsChange, isReadOnly } = props;

  const labelId = useId();
  const descriptionId = useId();
  const errorId = useId();

  const handleResponseChange = (value: string): void => {
    onSettingsChange({ mockResponse: value, mockResponseError: undefined });
  };

  return (
    <div>
      <Label id={labelId} size={Size.SMALL}>
        Mock response (optional)
      </Label>
      <p id={descriptionId} className="mt-1 text-sm text-muted-foreground">
        Response to return when this tool is called during a simulation. If not
        provided, a default simulation message will be used.
      </p>
      <div className="mt-2 flex gap-2">
        <div className="w-28">
          <SimpleSelect
            options={mockResponseTypeOptions}
            value={settings.mockResponseType}
            onChange={(value) => onSettingsChange({ mockResponseType: value })}
            isLabelHidden
            label="Response type"
          />
        </div>
        <div className="min-w-0 flex-1">
          {settings.mockResponseType === CustomToolMockResponseType.JSON ? (
            <>
              <LazyCodeEditor
                language="json"
                isReadOnly={isReadOnly}
                minHeight={80}
                placeholder='{"status": "success", "data": {...}}'
                value={settings.mockResponse}
                onChange={handleResponseChange}
                labelId={labelId}
                describedBy={`${descriptionId} ${errorId}`}
                hasError={settings.mockResponseError !== undefined}
              />
              {settings.mockResponseError ? (
                <FieldError id={errorId}>
                  {settings.mockResponseError}
                </FieldError>
              ) : null}
            </>
          ) : (
            <Textarea
              id="mockResponse"
              value={settings.mockResponse}
              maxLength={toolMockResponseMaxLength}
              onChange={handleResponseChange}
              placeholder="Tool executed successfully"
              errorDescription={settings.mockResponseError}
            />
          )}
        </div>
      </div>
    </div>
  );
};
